import React, { useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';

import {
  Container,
  SearchBox,
  SearchInput,
  List,
  Item,
  ItemIndex,
  ItemLabel,
  EmptyHint,
  Count,
} from './styles';

export interface ContentPickerItem {
  /** Chave estável do item. */
  id: string;
  /** Texto principal exibido. */
  label: string;
  /** Texto secundário opcional. */
  hint?: string;
}

interface ContentPickerProps {
  items: ContentPickerItem[];
  /** Id do item selecionado (ou '' para nenhum). */
  selectedId: string;
  onSelect: (id: string) => void;
  loading?: boolean;
  /** Título da lista (ex.: "Aulas já cadastradas neste módulo"). */
  title?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
}

/**
 * Lista filtrável dos itens já existentes. Clicar num item o seleciona e
 * popula o formulário; é o "pesquisar conteúdo existente" do editor.
 */
const ContentPicker: React.FC<ContentPickerProps> = ({
  items,
  selectedId,
  onSelect,
  loading = false,
  title = 'Itens já cadastrados',
  emptyMessage = 'Nenhum item cadastrado ainda.',
  searchPlaceholder = 'Buscar…',
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.label.toLocaleLowerCase().includes(query) ||
        (item.hint ?? '').toLocaleLowerCase().includes(query),
    );
  }, [items, search]);

  return (
    <Container>
      <SearchBox>
        <FiSearch size={16} aria-hidden />
        <SearchInput
          type="search"
          value={search}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          onChange={(event) => setSearch(event.target.value)}
        />
      </SearchBox>

      <Count>
        {title} · {filtered.length}
        {filtered.length !== items.length && ` de ${items.length}`}
      </Count>

      <List role="listbox" aria-label={title}>
        {loading && <EmptyHint>Carregando…</EmptyHint>}

        {!loading && filtered.length === 0 && (
          <EmptyHint>
            {items.length === 0 ? emptyMessage : 'Nenhum item encontrado para essa busca.'}
          </EmptyHint>
        )}

        {!loading &&
          filtered.map((item, index) => (
            <Item
              key={item.id}
              type="button"
              role="option"
              aria-selected={item.id === selectedId}
              selected={item.id === selectedId}
              onClick={() => onSelect(item.id)}
            >
              <ItemIndex>{index}</ItemIndex>
              <ItemLabel>
                {item.label}
                {item.hint && <small>{item.hint}</small>}
              </ItemLabel>
            </Item>
          ))}
      </List>
    </Container>
  );
};

export default ContentPicker;
